/**
 * Kennzahlen der Liquiditaetsplanung als schlanke Bilanzkarte "Verfuegbar gegen Bedarf":
 * VERFUEGBAR (Kontostand + offene Sicherungen), BEDARF (fix + geplant + ohne Datum), beide Balken
 * auf gemeinsamer Skala, darunter das Ergebnis (Reichweite und Differenz).
 * Die Bedarf-Balkensegmente und die Chips sind Knoepfe und filtern die Ratentabelle
 * (Faellig fix, Geplant, Ohne Datum, Ueberfaellig, Stock); ein zweiter Klick hebt auf.
 * Der Verfuegbar-Balken ist reine Anzeige.
 */
import { Ikone } from "../Ikone.tsx";
import { fmtUsd, NUM } from "../../lib/format.ts";
import { fxWeekChartValue } from "../../lib/fx.ts";
import { wochenLabel, type KachelFilter, type LiquiditaetsKennzahlen } from "../../lib/liquiditaet.ts";

export interface KennzahlenProps {
  kennzahlen: LiquiditaetsKennzahlen;
  /** `false` solange der Kontostand nicht geladen ist: die Karte zeigt dann "–" statt $ 0. */
  kontostandBekannt: boolean;
  aktiv: KachelFilter | null;
  onKachel: (kachel: KachelFilter) => void;
  /** Wochenreihe hat mindestens eine Woche - sonst gibt es keine Aussage zur Reichweite. */
  hatWochen: boolean;
}

export function Kennzahlen({ kennzahlen: k, kontostandBekannt, aktiv, onKachel, hatWochen }: KennzahlenProps) {
  // Bedarf = fix + geplant + ohne Datum: die drei Klassen sind disjunkt und decken alle offenen Raten ab.
  // Stock und Ueberfaellig sind TEILMENGEN dieser Klassen ("davon") und werden bewusst NICHT addiert.
  const bedarf = k.fixSumme + k.geplantSumme + k.ohneDatumSumme;
  const verfuegbar = k.gesichertGesamt;
  const differenz = verfuegbar - bedarf;
  // Gemeinsame Skala beider Balken; bei 0 bleiben die Balken leer (keine Division durch 0).
  const skala = Math.max(verfuegbar, bedarf);
  const pct = (wert: number): number => (skala > 0 ? Math.max(0, Math.min(100, (wert / skala) * 100)) : 0);

  const segmente: { kachel: KachelFilter; label: string; summe: number; ton: string }[] = [
    { kachel: "fix", label: "Fällig fix", summe: k.fixSumme, ton: "warning" },
    { kachel: "geplant", label: "Geplant", summe: k.geplantSumme, ton: "info" },
    { kachel: "ohneDatum", label: "Ohne Datum", summe: k.ohneDatumSumme, ton: "urgent" },
  ];
  const chip = (kachel: KachelFilter, label: string, summe: number, n: number, extra = "") => (
    <button
      key={kachel}
      type="button"
      className={`liq-bilanz-chip${extra}${aktiv === kachel ? " aktiv" : ""}`}
      aria-pressed={aktiv === kachel}
      onClick={() => onKachel(kachel)}
    >
      {kachel === "ueberfaellig" && n > 0 ? <Ikone name="triangle-alert" /> : null}
      <span className="liq-bilanz-chip-label">{label}</span>
      <b>{fmtUsd(summe)}</b>
      <span className="liq-bilanz-chip-anzahl">{NUM.format(n)}</span>
    </button>
  );

  // Reichweite = erste Woche mit negativem kumuliertem Saldo. Die Aussage nennt sie als
  // "Unterdeckung ab ..." und darunter die letzte noch gedeckte Woche mit dem Saldo der Unterdeckungswoche.
  let reichweiteText: string;
  let reichweiteNotiz: string | null = null;
  if (!hatWochen) reichweiteText = "Keine Planwerte";
  else if (!k.reichweite) reichweiteText = "Gedeckt über den Planungshorizont";
  else if (k.reichweite.ueberfaellig) reichweiteText = "Gesichert deckt das Überfällige nicht";
  else if (k.reichweite.spaeter) {
    reichweiteText = "Unterdeckung erst nach dem Planungshorizont";
    if (k.gedecktBis) reichweiteNotiz = `gedeckt bis ${wochenLabel(k.gedecktBis)}`;
  } else {
    reichweiteText = `Unterdeckung ab ${wochenLabel(k.reichweite)}`;
    const saldo = `−${fxWeekChartValue(Math.abs(k.reichweite.saldo))}`;
    reichweiteNotiz = k.gedecktBis ? `gedeckt bis ${wochenLabel(k.gedecktBis)} · ${saldo}` : `schon in der aktuellen Woche · ${saldo}`;
  }
  const reichweiteTon = k.reichweite && !k.reichweite.ueberfaellig ? "warning" : k.reichweite ? "urgent" : "good";

  return (
    <div className="liq-bilanz" role="group" aria-label="Kennzahlen">
      <div className="liq-bilanz-zeile">
        <div className="liq-bilanz-label">Verfügbar</div>
        <div className="liq-bilanz-betrag good">{fmtUsd(verfuegbar)}</div>
        <div className="liq-bilanz-rechts">
          <div className="liq-balken" aria-hidden="true">
            {k.kontostand > 0 ? <span className="liq-balken-teil konto" style={{ width: `${pct(k.kontostand)}%` }} /> : null}
            {k.offeneSicherungen > 0 ? <span className="liq-balken-teil sicherung" style={{ width: `${pct(k.offeneSicherungen)}%` }} /> : null}
          </div>
          <div className="liq-bilanz-legende">
            <span><i className="liq-punkt konto" aria-hidden="true" />Kontostand {kontostandBekannt ? NUM.format(k.kontostand) : "–"}</span>
            <span><i className="liq-punkt sicherung" aria-hidden="true" />offene Sicherungen {NUM.format(k.offeneSicherungen)}</span>
            {kontostandBekannt ? null : <span className="liq-bilanz-hinweis">Kontostand nicht geladen</span>}
          </div>
        </div>
      </div>
      <div className="liq-bilanz-zeile">
        <div className="liq-bilanz-label">
          Bedarf
          <span className="liq-bilanz-klickhinweis"><Ikone name="filter" />Klick filtert Raten</span>
        </div>
        <div className="liq-bilanz-betrag">{fmtUsd(bedarf)}</div>
        <div className="liq-bilanz-rechts">
          <div className="liq-balken" role="group" aria-label="Bedarf nach Klasse">
            {segmente.map((s) =>
              s.summe > 0 ? (
                <button
                  key={s.kachel}
                  type="button"
                  className={`liq-balken-teil ${s.ton}${aktiv === s.kachel ? " aktiv" : aktiv ? " abgeblendet" : ""}`}
                  style={{ width: `${pct(s.summe)}%` }}
                  aria-pressed={aktiv === s.kachel}
                  aria-label={`Balken: ${s.label}`}
                  title={`${s.label} ${fmtUsd(s.summe)}`}
                  onClick={() => onKachel(s.kachel)}
                />
              ) : null,
            )}
          </div>
          <div className="liq-bilanz-chips">
            {chip("fix", "Fällig fix", k.fixSumme, k.fixAnzahl, " warning")}
            {chip("geplant", "Geplant", k.geplantSumme, k.geplantAnzahl, " info")}
            {chip("ohneDatum", "Ohne Datum", k.ohneDatumSumme, k.ohneDatumAnzahl, " urgent")}
            <span className="liq-bilanz-davon">davon</span>
            {chip("ueberfaellig", "Überfällig", k.ueberfaelligSumme, k.ueberfaelligAnzahl, k.ueberfaelligAnzahl > 0 ? " rot" : "")}
            {chip("stock", "Stock", k.stockSumme, k.stockAnzahl)}
          </div>
        </div>
      </div>
      <div className="liq-bilanz-ergebnis">
        <div className={`liq-bilanz-reichweite ${reichweiteTon}`}>
          <div className="liq-reichweite-text">{reichweiteText}</div>
          {reichweiteNotiz ? <div className="liq-bilanz-notiz">{reichweiteNotiz}</div> : null}
        </div>
        <div className="liq-bilanz-differenz">
          <span className="liq-bilanz-notiz">Differenz gesamt</span>
          <b className={differenz < 0 ? "urgent" : "good"}>
            {differenz < 0 ? `−${fmtUsd(Math.abs(differenz))}` : fmtUsd(differenz)}
          </b>
        </div>
      </div>
    </div>
  );
}
